/**
 * DemoTour - guided walkthrough overlay with two modes.
 *
 *   ?demo=tour  - 12-step sales tour (Discovery → Activity → Detection →
 *                 Frontier). What to say, what to point at. Designed so
 *                 anyone can run the customer pitch end-to-end without
 *                 prior product knowledge.
 *
 *   ?demo=ops   - 10-step operator tour (Phase 10 landing tour). Walks a
 *                 first-time DPO / IT Admin through the admin + compliance
 *                 surfaces that shipped through Phases 5b-9: Compliance
 *                 Center, Notices, Erasure, Breaches, Custom Roles wizard,
 *                 Edge Fleet, Edge Provisioning, Egress Log, Business
 *                 Units, Adapters. Goal: by the end, the operator knows
 *                 where every governance lever lives.
 *
 * Renders a floating panel with:
 *   - Current step number + total
 *   - Step title + narrative (mode-specific framing)
 *   - Back / Next / Skip controls
 *   - Auto-navigation between routes on Next/Back
 *
 * Per-mode storage key - switching tours preserves your place in each.
 */
import { useEffect, useState } from 'react';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import { motion, AnimatePresence } from 'motion/react';
import { ChevronLeft, ChevronRight, X, Sparkles, Compass } from 'lucide-react';

interface TourStep {
  route: string;
  title: string;
  /** Narrative for this step. Mode-specific framing (sales vs operator). */
  say: string;
  /** What to point at / click while on this screen. */
  point: string;
}

const SALES_STEPS: ReadonlyArray<TourStep> = [
  {
    route: '/',
    title: '1. The narrative - IndiaPay Capital',
    say: 'IndiaPay is a fictional Indian fintech / NBFC, RBI-licensed. This is the FOCTTA Platform showcase tenant. The data you see is the same shape your environment will look like on day-1 of your own deployment - DSPM has scanned 50 assets, DAM is streaming activity events, DDR is making real-time decisions.',
    point: 'The four-module roll-up on the home page: Discovery, Activity, Detection, Frontier.',
  },
  {
    route: '/dspm/inventory',
    title: '2. Discovery - what we found',
    say: '50 data assets across 9 source types - Postgres, MSSQL, Oracle, MongoDB, Snowflake (US, cross-border), BigQuery, Databricks, Redshift, S3, Azure Blob, SharePoint. Discovery ran without direct DB access - it bootstraps from cloud APIs, CMDB, and SSO logs.',
    point: 'Scroll the inventory table. Notice the source-type diversity.',
  },
  {
    route: '/dspm/findings',
    title: '3. The findings - 35 issues, prioritized by risk',
    say: 'Every finding is backed by a real Indian regulation - DPDPA Section 9 (children’s data), DPDPA Section 16 (cross-border), UIDAI 2025 Circular (Aadhaar), RBI Master Direction Cyber Security, IT Act 2000, GST Act. The Tier-1 classifier validates with Verhoeff (Aadhaar) and Luhn (PAN) checksums.',
    point: 'Filter to Critical. The top one is "Aadhaar + minor_dob in same row, 12,408 minor records".',
  },
  {
    route: '/dspm/section-9',
    title: '4. The Section 9 lens - children’s data',
    say: 'DPDPA Section 9 requires explicit verifiable parental consent for any processing of a data subject under 18, AND a hard-block on cross-border transfer. We don’t just detect - we map every minor-data row to the consent record. If consent is missing, the row is flagged for purge.',
    point: 'The "12,408 minor records, 0 with parental consent on file" number. That’s a real compliance breach.',
  },
  {
    route: '/dspm/reverse-dsar',
    title: "5. Find a person's data - show me everywhere Aanya lives",
    say: 'Subject data discovery walks the column-graph: given an identifier (Aadhaar, PAN, email, phone), it finds every asset that holds that identifier. This is what answers a DPDPA Section 11 access request in seconds, not weeks.',
    point: 'Try Aadhaar 999988887777 → returns 3 assets including the cross-border Snowflake table. That’s the toxic combination.',
  },
  {
    route: '/activity/ueba',
    title: '6. UEBA - the insider kill-chain unfolds',
    say: 'Look at the timeline. 3 days ago, Aanya Sharma - a Senior Data Analyst recently put on a PIP - started probing tables outside her normal scope. Day -2 she expanded to audit_logs (reconnaissance). Day -1 at 02:14 UTC she hit the Aadhaar vault - first 3σ critical. This is Western Electric Rule-1: 3 standard deviations above her own 14-day baseline.',
    point: 'The rule-1 critical alert. Compare with svc.batch-export firing today (rule-4 8-on-side).',
  },
  {
    route: '/activity/sonar',
    title: '7. Sonar - acoustic drift on services',
    say: 'UEBA tracks individual actors. Sonar tracks service signatures - the "acoustic" pattern of how a service queries the database. svc.legacy-batch has drifted: KL divergence 0.42 vs its 14-day baseline, new tables in its access pattern. Different mechanism, same goal: catch what you didn’t know to look for.',
    point: 'The KL-divergence chart. svc.legacy-batch picked up finance.accounts which it never touched before.',
  },
  {
    route: '/activity/p-wave',
    title: '8. Mass-Export Pre-Cog - the warning shot',
    say: 'Today at 03:42 UTC, svc.batch-export - which we now suspect Aanya compromised - fired a SELECT * FROM customers query that would have returned 1.24 million rows. The P-wave engine predicts the export volume in real-time. The threshold for "mass export" is set by industry pack - for fintech, it’s 10K rows.',
    point: 'The volume projection chart. Watch the P-wave curve cross the threshold.',
  },
  {
    route: '/detection/decisions',
    title: '9. DDR - the block, in real time',
    say: 'At 03:42:18 UTC, the SQL proxy sidecar saw the mass-export query before it executed against Postgres. The Aerospace Triple Redundancy engine voted: OPA rule said block (mass-export), UEBA said block (rule-1 open for the operating actor), Policy Graph said block (Section 9 minor data in target). All three agree → hard-block. The customer app got a synthetic SQL error in <5ms.',
    point: 'The decisions ticker. Find the block - request id BLK-7K9PQ-2026-05-12 - and click into it.',
  },
  {
    route: '/detection/decisions',
    title: '10. The Synthetic-Error Contract',
    say: 'Here’s the trick. ONE request id - BLK-7K9PQ-2026-05-12 - appears in: the synthetic Postgres error the app got, the DDR decision record, the hash-chained audit chain, the Splunk SIEM ticket draft, the DPO’s breach console pre-fill, and the SOC runbook step. No translation tables, no cross-system manual joins. The whole organisation sees the same event by the same id. That’s the kill switch and the audit trail in one move.',
    point: 'The "view in audit chain" link. Verify the BLK-7K9PQ id matches across surfaces.',
  },
  {
    route: '/frontier/smpc',
    title: '11. Frontier - Privacy-Preserving Benchmarks',
    say: 'IndiaPay wants to know: "How does our Section 9 violation rate compare to the rest of the fintech sector?" Normally that would require sharing data. SMPC - Secure Multi-Party Computation - gives the aggregate without anyone sharing inputs. Click Run SMPC roundtrip. Watch the aggregate appear with a SHA-256 payload hash - nobody, including FOCTTA, saw the per-tenant numbers.',
    point: 'Click Run SMPC roundtrip → 1500 aggregate over 5 participants, k≥3 anonymity.',
  },
  {
    route: '/frontier/replay',
    title: '12. Compliance Replay - counter-factual time travel',
    say: 'Final piece. The Compliance Replay Engine asks: "If RBI 6h breach clock had applied 90 days earlier, what changes?" It replays your last 30 days under any of 15 regulatory frames - DPDPA, GDPR, RBI, SEBI, HIPAA, PCI-DSS, UIDAI, PIPL, LGPD, PIPEDA, Singapore PDPA, and more. Click Run replay. Each regulator returns its delta: clock tightenings, new hard-blocks, Section-9-equivalent flips.',
    point: 'Click Run replay → per-regulator deltas. End the tour with: "This is what FOCTTA does. Want to talk about your environment?"',
  },
];

// ─────────────────────────────────────────────────────────────────────
// Operator tour (Phase 10 landing tour)
// ─────────────────────────────────────────────────────────────────────
//
// Aimed at a DPO / IT Admin opening the platform for the first time on
// their tenant. Walks them through every governance surface in roughly
// the order they’d touch them during initial set-up + day-2 operations.
// Each step has a short framing - what this surface IS, why they’d
// come back to it, and the one concrete action they should try right now.

const OPS_STEPS: ReadonlyArray<TourStep> = [
  {
    route: '/',
    title: '1. Welcome - your governance control plane',
    say: 'This is the home of every governance lever in FOCTTA. Discovery / Activity / Detection are the operator-facing dashboards; the admin and compliance pages are where you SET UP and TUNE the controls. This tour walks you through every admin surface in the order you’d use them.',
    point: 'The Settings & Admin and Compliance nav groups in the sidebar.',
  },
  {
    route: '/compliance',
    title: '2. Compliance Center - your DPO landing page',
    say: 'The Compliance Center is the at-a-glance view of every obligation you have to meet. Coverage shows what regulations are mapped to which assets. Gaps shows what’s missing. Export gives you the auditor-ready PDF + JSONB pack. Most DPOs start their day here.',
    point: 'The coverage / gaps / export tabs at the top. Click each once to see the shapes.',
  },
  {
    route: '/compliance/notices',
    title: '3. Notices - Section 6 / Art 13 lifecycle',
    say: 'Every privacy notice you publish has a lifecycle - draft, reviewed, published, retired - and a tamper-evident SHA-256 of the published body. State machine enforced server-side. You can’t un-publish; you can only supersede with a new version. Auditors love this.',
    point: 'The state-machine column on the list. Open one notice and look at the version history.',
  },
  {
    route: '/compliance/erasure',
    title: '4. Erasure - DPDPA §12 / GDPR Art 17 right-to-be-forgotten',
    say: 'Erasure jobs walk the access graph from a subject identifier (Aadhaar, PAN, email) and queue every matching row across every connected system for deletion. State machine: requested → verified → scheduled → executed → confirmed. Each transition is in the audit chain.',
    point: 'The state-machine column. Look at how many systems each job touches.',
  },
  {
    route: '/compliance/rectification',
    title: '5. Rectification - DPDPA §13 / GDPR Art 16 right-to-correct',
    say: 'Same shape as erasure, but the subject is asking us to CORRECT data, not delete it. Same access-graph walk, same DPO approval gate, same audit chain. The corrections map (field path → new value) is persisted verbatim so the auditor can verify exactly what the subject requested.',
    point: 'Open one job. Notice the per-asset execution log lists which fields were updated.',
  },
  {
    route: '/compliance/breaches',
    title: '6. Breach Dispatch - RBI 6-hour / GDPR 72-hour clock',
    say: 'When DDR registers a block (Phase 5 sales-tour step 9), the breach console pre-fills here automatically - same request id, same audit chain. From here the DPO drives the regulator notification within the statutory clock. The dispatch lifecycle is end-to-end: prepared → dispatched → acknowledged.',
    point: 'The 6-hour countdown badge. Then the "view in audit chain" link on any breach.',
  },
  {
    route: '/admin/business-units',
    title: '7. Business Units - your org tree',
    say: 'Business Units are the canonical org tree your RBAM scopes against. Synced from FOCTTA core via the resync button. Every other admin surface - RBAM, Custom Roles, scope envelopes - references these. If your tree changes, resync first.',
    point: 'The "Resync from FOCTTA core" button. Then the BU hierarchy tree.',
  },
  {
    route: '/admin/rbam',
    title: '8. RBAM Matrix - the 6 standard roles',
    say: 'RBAM Matrix shows which platform permissions each of the 6 standard roles has - DPO, Compliance Analyst, Legal Reviewer, IT Admin, Security Operator, Read-Only Sec Ops. These are LOCKED. They define the floor. Tenant-specific scoping happens through Custom Roles next.',
    point: 'Hover any permission. The role-by-role grant grid.',
  },
  {
    route: '/admin/custom-roles',
    title: '9. Custom Roles - tenant-authored RBAM with 2P approval',
    say: 'This is the surface you’ll touch most. Author a role with any subset of platform permissions plus a scope envelope (BU × system × data class × purpose). Then a second human - DPO or IT Admin, NOT the creator - approves it. The 2-person rule is enforced at THREE layers: UI, service, and a DB CHECK constraint.',
    point: 'Click "+ Create role". Try the wizard. Notice the permission catalogue grouped by module.',
  },
  {
    route: '/admin/edge-fleet',
    title: '10. Edge Fleet - your scanner pods',
    say: 'Edge pods are the customer-VPC scanners that do the actual data inspection without ever shipping raw bytes to FOCTTA. The fleet view shows every pod’s health, version, last heartbeat, and which assets it has scoped. If a pod goes red, the asset it covered stops being scanned until you fix it.',
    point: 'The pod cards. Click into a pod to see its last 50 heartbeats.',
  },
  {
    route: '/admin/edge-provisioning',
    title: '11. Edge Provisioning - adding a new pod',
    say: 'When you want to scan a new VPC or on-prem segment, you provision a pod here. Output: a single-use bootstrap token + a docker-compose snippet. Drop the snippet into the target VPC, it phones home, the pod shows up in the fleet view (step 9). End-to-end in under 5 minutes.',
    point: 'Click "+ Provision new pod". Look at the generated bootstrap token - single-use, 24-hour expiry, never persisted.',
  },
];

const STORAGE_KEY_SALES = 'foctta.demo.tour.step';
const STORAGE_KEY_OPS = 'foctta.demo.ops.step';
/**
 * OBS-16 (2026-05-26) - once the user has seen the tour (skipped or
 * completed), don't auto-launch on subsequent visits. Set to '1' on
 * skip / completion. Tied to localStorage so it survives reloads but
 * is per-browser (a different evaluator on a different device gets a
 * fresh auto-launch - desirable).
 */
const STORAGE_KEY_SEEN = 'foctta.demo.tour.seen';

type TourMode = 'sales' | 'ops';

function resolveMode(param: string | null): TourMode | null {
  if (param === 'tour') return 'sales';
  if (param === 'ops') return 'ops';
  return null;
}

function stepsForMode(mode: TourMode): ReadonlyArray<TourStep> {
  return mode === 'ops' ? OPS_STEPS : SALES_STEPS;
}

function storageKeyForMode(mode: TourMode): string {
  return mode === 'ops' ? STORAGE_KEY_OPS : STORAGE_KEY_SALES;
}

export function DemoTour(): JSX.Element | null {
  const router = useRouterState();
  const navigate = useNavigate();
  const params = new URLSearchParams(router.location.search);
  const mode = resolveMode(params.get('demo'));
  const tourActive = mode !== null;

  // OBS-16 (2026-05-26) - auto-launch the sales tour on a first-time
  // evaluator's landing on `/`. Guards:
  //   • Only fires when there's no ?demo= param (tourActive=false)
  //   • Only on the landing route - interrupting a deep-linked user
  //     with a tour they didn't ask for would be hostile
  //   • Only when STORAGE_KEY_SEEN isn't set - once they skip or
  //     complete once, they don't get auto-launched again
  //   • Defers the navigation to the next tick so React's mount
  //     phase finishes (avoids the "navigate during render" warning)
  useEffect(() => {
    if (tourActive) return;
    if (router.location.pathname !== '/') return;
    if (typeof window === 'undefined') return;
    try {
      if (localStorage.getItem(STORAGE_KEY_SEEN)) return;
    } catch {
      return;
    }
    const t = setTimeout(() => {
      navigate({ to: '/', search: { demo: 'tour' } as Record<string, unknown> });
    }, 0);
    return () => clearTimeout(t);
  }, [tourActive, router.location.pathname, navigate]);

  const steps = mode ? stepsForMode(mode) : SALES_STEPS;
  const storageKey = mode ? storageKeyForMode(mode) : STORAGE_KEY_SALES;

  const [step, setStep] = useState<number>(() => {
    if (!mode) return 0;
    const stored = typeof window !== 'undefined' ? localStorage.getItem(storageKeyForMode(mode)) : null;
    return stored
      ? Math.max(0, Math.min(stepsForMode(mode).length - 1, parseInt(stored, 10)))
      : 0;
  });

  // Reset step counter when the user switches modes mid-session (rare,
  // but ?demo=tour → ?demo=ops in the same tab would otherwise carry over
  // an out-of-range index).
  useEffect(() => {
    if (!mode) return;
    const max = stepsForMode(mode).length - 1;
    setStep((s) => (s > max ? 0 : s));
  }, [mode]);

  useEffect(() => {
    if (!tourActive) return;
    localStorage.setItem(storageKey, String(step));
  }, [step, tourActive, storageKey]);

  useEffect(() => {
    if (!tourActive || !mode) return;
    const target = steps[step]!;
    if (router.location.pathname !== target.route) {
      navigate({ to: target.route, search: { demo: mode === 'ops' ? 'ops' : 'tour' } as Record<string, unknown> });
    }
  }, [step, tourActive, navigate, router.location.pathname, mode, steps]);

  if (!tourActive || !mode) return null;
  const current = steps[step]!;

  function next(): void {
    if (step < steps.length - 1) setStep((s) => s + 1);
  }
  function prev(): void {
    if (step > 0) setStep((s) => s - 1);
  }
  function skip(): void {
    localStorage.removeItem(storageKey);
    // OBS-16: mark as seen so we don't auto-launch again. The
    // explicit Start-tour button still works (sets ?demo=tour
    // manually) - only the implicit auto-launch is suppressed.
    try {
      localStorage.setItem(STORAGE_KEY_SEEN, '1');
    } catch {
      /* private-browsing storage denied - fall through */
    }
    navigate({ to: router.location.pathname, search: {} });
  }

  const isFinal = step === steps.length - 1;
  const modeLabel = mode === 'ops' ? 'Operator tour' : 'Demo tour';
  const ModeIcon = mode === 'ops' ? Compass : Sparkles;

  return (
    <AnimatePresence>
      <motion.div
        key="tour-overlay"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 20 }}
        transition={{ duration: 0.3, ease: [0.32, 0.72, 0, 1] }}
        className="fixed bottom-6 right-6 z-50 w-[420px] max-w-[calc(100vw-3rem)]"
      >
        <div className="glass-strong rounded-xl p-5 shadow-2xl border border-primary/30 backdrop-blur-xl">
          {/* Header */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <ModeIcon className="h-4 w-4 text-primary" />
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{modeLabel}</span>
              <span className="text-[10px] tabular text-muted-foreground">step {step + 1} of {steps.length}</span>
            </div>
            <button
              onClick={skip}
              className="text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Skip tour"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Progress bar */}
          <div className="h-1 bg-muted/30 rounded-full mb-4 overflow-hidden">
            <motion.div
              className="h-full bg-gradient-to-r from-primary to-chart-2"
              initial={{ width: '0%' }}
              animate={{ width: `${((step + 1) / steps.length) * 100}%` }}
              transition={{ duration: 0.4 }}
            />
          </div>

          {/* Title */}
          <h3 className="text-sm font-semibold mb-2">{current.title}</h3>

          {/* What to say */}
          <p className="text-xs text-muted-foreground leading-relaxed mb-3">
            <span className="text-foreground">{mode === 'ops' ? 'Context:' : 'Say:'}</span> {current.say}
          </p>

          {/* What to point at */}
          <p className="text-[11px] text-primary/80 italic mb-4">
            <span className="not-italic">{mode === 'ops' ? 'Try:' : 'Point at:'}</span> {current.point}
          </p>

          {/* Controls */}
          <div className="flex items-center justify-between gap-2">
            <button
              onClick={prev}
              disabled={step === 0}
              className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-md border border-border bg-muted/30 hover:bg-muted/50 disabled:opacity-30 transition-colors"
            >
              <ChevronLeft className="h-3 w-3" /> Back
            </button>
            <button
              onClick={skip}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              Skip
            </button>
            {/* OBS-16: Done button on the final step actually completes
                the tour (was disabled before, so the user could only
                Skip). Completion sets the seen-flag so auto-launch
                doesn't re-fire. */}
            <button
              onClick={isFinal ? skip : next}
              className="flex items-center gap-1 text-xs px-4 py-1.5 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              {isFinal ? 'Done' : 'Next'} <ChevronRight className="h-3 w-3" />
            </button>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
